import { SyntheticEvent, useCallback, useContext } from 'react';
import { Products, Product } from 'models/products';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { api } from 'config/api';
import { useAuthenticatedApiCall } from './api.service';

const PRODUCT_IMAGE_NOT_AVAILABLE = '/illustrations/product-image-not-available.svg';
const baseConfig = {
  baseURL: api.API_URL,
};

export const useProductDataService = () => {
  const { makeAuthenticatedApiCall } = useAuthenticatedApiCall(baseConfig);
  const { accountNumber } = useContext(TenantConfigContext);

  const fetchProductDataResponse = useCallback(
    async (search: string) => {
      const { data, error } = await makeAuthenticatedApiCall<Products>({
        url: 'products',
        method: 'GET',
        params: {
          search,
        },
      });
      return { data, error };
    },
    [makeAuthenticatedApiCall, accountNumber]
  );

  const fetchProductsByNumbers = useCallback(
    async (numbers: string[]): Promise<Product[]> => {
      if (!numbers.length) return [];

      const responses = await Promise.all(numbers.map(number => fetchProductDataResponse(number)));

      const products = responses.map(res => res.data?.list?.[0]).filter((p): p is Product => Boolean(p));

      return products;
    },
    [fetchProductDataResponse]
  );

  return {
    fetchProductDataResponse,
    fetchProductsByNumbers,
  };
};

/**
 * Constructs a URL to retrieve an article image
 *
 * @param accountNumber account number to which the image belongs
 * @param url URL for the source image as provided by TB.one
 */
export const buildApiImageUrl = (accountNumber: string | null | undefined, url: string | undefined) => {
  let apiImageUrl = PRODUCT_IMAGE_NOT_AVAILABLE;
  if (url) {
    apiImageUrl = `${api.IMAGE_SERVER_URL}/v1/images/skus/thumbnail?url=${encodeURIComponent(url)}`;
    if (accountNumber) {
      apiImageUrl += `&accountNumber=${accountNumber}`;
    }
  }
  return apiImageUrl;
};

export const addImageNotAvailable = (ev: SyntheticEvent<HTMLImageElement>) => {
  const imageElement = ev.target as HTMLImageElement;
  imageElement.src = '/illustrations/image_not_available.svg';
  imageElement.className = imageElement.className.replace('object-cover', 'object-contain');
  imageElement.style.backgroundColor = '#F4F4F4';
};
